import React, { useState, useEffect } from "react";
import { 
  Card, 
  List, 
  Avatar, 
  Empty, 
  Tabs, 
  Tag, 
  Space, 
  Button, 
  Divider, 
  Badge, 
  Row, 
  Col, 
  Input, 
  Spin,
  message,
  Modal
} from "antd";
import { 
  GiftOutlined, 
  GlobalOutlined, 
  HistoryOutlined, 
  ClockCircleOutlined,
  UserAddOutlined,
  SearchOutlined,
  CopyOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next"; 
import {
  getUserCoupons,
  getGlobalCoupons,
  getAssignedCoupons,
  getUsedCoupons,
  getExpiredCoupons
} from "../../../services/dashboardclient/dashboardclient";

const { TabPane } = Tabs;
const { Search } = Input;

const Coupons = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("available");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState({
    available: [],
    offered: [],
    global: [],
    used: [],
    expired: []
  });

  // Fetch coupons based on active tab
  const fetchCoupons = async () => {
    setLoading(true);
    try {
      let data;
      switch (activeTab) {
        case "available":
          data = await getUserCoupons("active");
          const allAvailableCoupons = [
            ...(data.assigned || []),
            ...(data.global || [])
          ];
          setCoupons((prev) => ({ ...prev, available: allAvailableCoupons }));
          break;
        case "offered":
          data = await getAssignedCoupons();
          setCoupons((prev) => ({ ...prev, offered: data }));
          break;
        case "global":
          data = await getGlobalCoupons();
          setCoupons((prev) => ({ ...prev, global: data }));
          break;
        case "used":
          data = await getUsedCoupons();
          setCoupons((prev) => ({ ...prev, used: data }));
          break;
        case "expired":
          data = await getExpiredCoupons();
          setCoupons((prev) => ({ ...prev, expired: data }));
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
      message.error(t("Failed_to_load_coupons"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [activeTab]);

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        message.success(`${t("Copied")}: ${code}`);
      })
      .catch(() => {
        message.error(t("Failed_to_copy_code"));
      });
  };

  const formatDiscount = (coupon) => {
    if (coupon.type === "percentage") {
      return `${coupon.value}% ${t("off")}`;
    } else if (coupon.type === "fixed") {
      return `$${coupon.value} ${t("off")}`;
    }
    return coupon.description || t("No_discount");
  };

  const getCouponStatus = (coupon) => {
    const now = new Date();
    const validUntil = new Date(coupon.valid_until);

    if (coupon.pivot && coupon.pivot.used_count > 0) {
      return "used";
    } else if (validUntil < now) {
      return "expired";
    } else if (coupon.is_active) {
      return "active";
    }
    return "inactive";
  };

  const filteredCoupons = (coupons) => {
    if (!searchText) return coupons;
    return coupons.filter(
      (coupon) =>
        coupon.code.toLowerCase().includes(searchText.toLowerCase()) ||
        (coupon.description &&
          coupon.description.toLowerCase().includes(searchText.toLowerCase()))
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // 🎨 Background colors per status
  const statusColors = {
    active: "var(--success-light, #f6ffed)",
    used: "var(--info-light, #e6f7ff)",
    expired: "var(--error-light, #fff1f0)",
    inactive: "var(--neutral-light, #fafafa)"
  };

  const renderCouponList = (coupons) => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      );
    }

    const filtered = filteredCoupons(coupons);

    return filtered && filtered.length > 0 ? (
      <List
        itemLayout="horizontal"
        dataSource={filtered}
        renderItem={(item) => {
          const status = getCouponStatus(item);
          return (
            <List.Item
              style={{
                backgroundColor: statusColors[status],
                borderRadius: "8px",
                marginBottom: "10px",
                padding: "12px"
              }}
              actions={[
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyCode(item.code)}
                >
                  {t("Copy")}
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    icon={<GiftOutlined />}
                    style={{
                      backgroundColor:
                        status === "active"
                          ? "var(--success-color, #52c41a)"
                          : status === "used"
                          ? "var(--info-color, #1890ff)"
                          : status === "expired"
                          ? "var(--error-color, #ff4d4f)"
                          : "var(--neutral-color, #d9d9d9)"
                    }}
                  />
                }
                title={
                  <Space>
                    <span>{item.code || t("Unknown_Code")}</span>
                    {item.is_global && (
                      <Tag color="blue">
                        <GlobalOutlined /> {t("Global")}
                      </Tag>
                    )}
                    {!item.is_global && item.pivot && (
                      <Tag color="purple">
                        <UserAddOutlined /> {t("Personal")}
                      </Tag>
                    )}
                    {status === "used" && <Tag color="green">{t("Used")}</Tag>}
                    {status === "expired" && (
                      <Tag color="red">{t("Expired")}</Tag>
                    )}
                    {status === "inactive" && (
                      <Tag color="default">{t("Inactive")}</Tag>
                    )}
                  </Space>
                }
                description={
                  <div>
                    <div>
                      <strong>{formatDiscount(item)}</strong>
                    </div>
                    <div>
                      {t("Expires")}: {formatDate(item.valid_until)}
                    </div>
                    {/* {item.min_amount && (
                      <div>
                        {t("Minimum_order")}: ${item.min_amount}
                      </div>
                    )} */}
                    {item.description && <div>{item.description}</div>}
                  </div>
                }
              />
            </List.Item>
          );
        }}
      />
    ) : (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("No_coupons_found")}
        style={{ color: 'var(--primary-color3)'  }}
         className="custom-empty"
      />
    );
  };

  return (
    <div style={{ padding: "10px", backgroundColor:"var(--background-body)", minHeight: "100vh" }}>
      <Card
        title={
          <Space>
            <GiftOutlined style={{color:"var(--primary-color)"}}/>
            <span style={{ color: "var(--primary-color)" }}>{t("My_Coupons")}</span>
          </Space>
        }
        style={{ 
          borderRadius: "8px",
          backgroundColor:"var(--background-body)", 
          color:"var(--primary-color)",
          border: "1px solid var(--primary-color6)", 
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)" 
        }}
      >
        <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
          <Col xs={24} sm={12}>
            <Search
              placeholder={t("Search_coupons")}
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
        </Row>

        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" size="large">
          <TabPane
            key="available"
            tab={
              <span style={{ color: "var(--primary-color)" }}>
                <GiftOutlined />
                {t("Available_Coupons")}
                <Badge
                  count={coupons.available.length}
                  style={{ backgroundColor: "var(--success-color, #52c41a)", marginLeft: 5 }}
                />
              </span>
            }
          >
            {renderCouponList(coupons.available)}
          </TabPane>

          <TabPane
            key="offered"
            tab={
              <span style={{ color: "var(--primary-color)" }}>
                <UserAddOutlined />
                {t("Personal_Coupons")}
                <Badge
                  count={coupons.offered.length}
                  style={{ backgroundColor: "var(--info-color, #1890ff)", marginLeft: 5 }}
                />
              </span>
            }
          >
            {renderCouponList(coupons.offered)}
          </TabPane>

          <TabPane
            key="used"
            tab={
              <span style={{ color: "var(--primary-color)" }}>
                <HistoryOutlined />
                {t("Used_Coupons")}
                <Badge
                  count={coupons.used.length}
                  style={{ backgroundColor: "var(--warning-color, #faad14)", marginLeft: 5 }}
                />
              </span>
            }
          >
            {renderCouponList(coupons.used)}
          </TabPane>

          <TabPane
            key="expired"
            tab={
              <span style={{ color: "var(--primary-color)" }}>
                <ClockCircleOutlined />
                {t("Expired_Coupons")}
                <Badge
                  count={coupons.expired.length}
                  style={{ backgroundColor: "var(--error-color, #ff4d4f)", marginLeft: 5 }}
                />
              </span>
            }
          >
            {renderCouponList(coupons.expired)}
          </TabPane>
        </Tabs>

        <Divider />
      </Card>
    </div>
  );
};

export default Coupons;
